import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';

import AppSlideTab from 'components/App/AppSlideTab';
import Breadcrumb from 'components/App/Breadcrumb';
import Card from 'components/App/Card';
import MarketDemand from 'components/MarketDemand';
import RevenuaDistribution from 'components/RevenuaDistribution';
import GuestInsight from 'components/GuestInsight';
import AnalyticsPerformance from 'components/AnalyticsPerformance';

import cn from 'utils/classnames';
import { getParamsFormObject } from 'utils';
import INITIAL_VALUE from 'constants/initialValue';
import { ANALYTIC_TYPE, ANALYTIC_TYPE_LIST } from 'constants/analytics';

import styles from './AnalyticsOverview.module.scss';

const BREADCRUMB = [
  {
    name: 'Phân tích ',
    isActive: false,
    link: '',
  },
  {
    name: 'Phân tích',
    isActive: true,
    link: '',
  },
];

function AnalyticsOverview(props) {
  const {
    isLoadingGetInsightOverview,
    isLoadingGetRevenueDistributionOverview,
    isLoadingGetRevenueDistributionChart,

    insightOverview,
    revenuaDistributionOverview,
    revenuaDistributionChart,
    placeDetail,

    actionGetInsightOverview,
    actionGetRevenueDistributionOverview,
    actionGetRevenueDistributionChart,
    actionGetPlaceDetail,
  } = props;
  const history = useHistory();
  const { search } = useLocation();

  const searchQuery = React.useMemo(() => {
    const params = new URLSearchParams(search);

    return {
      place: params.get('place'),
      days: params.get('days'),
      type: params.get('type') || ANALYTIC_TYPE.EFFICIENCY,
    };
  }, [search]);

  const onChangeTab = React.useCallback(
    (type) => {
      history.push({
        search: getParamsFormObject({
          ...searchQuery,
          type,
        }),
      });
    },
    [history, searchQuery],
  );

  const activeIndex = React.useMemo(
    () => ANALYTIC_TYPE_LIST.findIndex((item) => item.value === searchQuery.type),
    [searchQuery],
  );

  const renderTabContent = React.useMemo(() => {
    switch (searchQuery.type) {
      case ANALYTIC_TYPE.EFFICIENCY:
        return <AnalyticsPerformance />;

      case ANALYTIC_TYPE.GUEST_INSIGHT:
        return (
          <GuestInsight
            isLoading={isLoadingGetInsightOverview}
            data={insightOverview}
          />
        );

      case ANALYTIC_TYPE.REVENUE_DISTRIBUTION:
        return (
          <RevenuaDistribution
            isLoading={isLoadingGetRevenueDistributionOverview
              || isLoadingGetRevenueDistributionChart}
            overview={revenuaDistributionOverview}
            chart={revenuaDistributionChart}
          />
        );

      case ANALYTIC_TYPE.MARKET_DEMAND:
        return <MarketDemand placeDetail={placeDetail} />;

      default:
        return null;
    }
  }, [
    isLoadingGetInsightOverview,
    isLoadingGetRevenueDistributionOverview,
    isLoadingGetRevenueDistributionChart,
    searchQuery.type,
    insightOverview,
    revenuaDistributionOverview,
    revenuaDistributionChart,
    placeDetail,
  ]);

  React.useEffect(() => {
    if (searchQuery.place) {
      actionGetPlaceDetail({ placeId: searchQuery.place });
      actionGetInsightOverview({
        days: searchQuery.days,
        place: searchQuery.place,
      });
      actionGetRevenueDistributionOverview({
        days: searchQuery.days,
        place: searchQuery.place,
      });
      actionGetRevenueDistributionChart({
        days: searchQuery.days,
        place: searchQuery.place,
      });
    }
  }, [
    searchQuery.days,
    searchQuery.place,
    actionGetInsightOverview,
    actionGetRevenueDistributionChart,
    actionGetRevenueDistributionOverview,
    actionGetPlaceDetail,
  ]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb
        pageTitle="PHÂN TÍCH"
        breadcrumbList={BREADCRUMB}
      />

      <Card className={styles.SlideCard2}>
        <AppSlideTab
          activeIndex={activeIndex}
          listTab={ANALYTIC_TYPE_LIST}
          onSetValue={onChangeTab}
        />
      </Card>

      {renderTabContent}
    </div>
  );
}

AnalyticsOverview.propTypes = {
  isLoadingGetInsightOverview: PropTypes.bool.isRequired,
  isLoadingGetRevenueDistributionOverview: PropTypes.bool.isRequired,
  isLoadingGetRevenueDistributionChart: PropTypes.bool.isRequired,

  insightOverview: PropTypes.instanceOf(Object),
  revenuaDistributionOverview: PropTypes.instanceOf(Object),
  revenuaDistributionChart: PropTypes.instanceOf(Object),
  placeDetail: PropTypes.instanceOf(Object),

  actionGetInsightOverview: PropTypes.func.isRequired,
  actionGetRevenueDistributionOverview: PropTypes.func.isRequired,
  actionGetRevenueDistributionChart: PropTypes.func.isRequired,
  actionGetPlaceDetail: PropTypes.func.isRequired,
};

AnalyticsOverview.defaultProps = {
  insightOverview: INITIAL_VALUE.OBJECT,
  revenuaDistributionOverview: INITIAL_VALUE.OBJECT,
  revenuaDistributionChart: INITIAL_VALUE.OBJECT,
  placeDetail: INITIAL_VALUE.OBJECT,
};

export default AnalyticsOverview;
